/**
 * @file enterpriseSchemas.ts
 * @brief Zod schemas for company Enterprise shared-pool status (26-07-2026).
 */

import { z } from "zod";

export const EnterpriseAdvanceSchema = z
  .object({
    status: z.string().optional().nullable(),
    amountMinor: z.number().nullable().optional(),
    currency: z.string().nullable().optional(),
    rateMinor: z.number().nullable().optional(),
    minutes: z.number().nullable().optional(),
    paymentIntentId: z.string().nullable().optional(),
    chargedAt: z.union([z.string(), z.date()]).nullable().optional(),
  })
  .passthrough()
  .nullable()
  .optional();

export const EnterpriseAdvanceQuoteSchema = z
  .object({
    includedMinutes: z.number(),
    currency: z.string(),
    miRateMinor: z.number(),
    notesRateMinor: z.number(),
    poolRateMinor: z.number(),
    amountMinor: z.number(),
    formula: z.string().optional(),
    miSource: z.string().optional(),
    notesSource: z.string().optional(),
    status: z.string().optional(),
    charged: z.boolean().optional(),
    message: z.string().optional(),
    paymentIntentId: z.string().nullable().optional(),
    chargeAmountMinor: z.number().optional(),
    chargeMinutes: z.number().optional(),
  })
  .passthrough()
  .nullable()
  .optional();

export const EnterprisePlanSchema = z
  .object({
    id: z.string(),
    companyId: z.string(),
    status: z.string(),
    seatLimit: z.number(),
    includedMinutes: z.number(),
    periodStart: z.union([z.string(), z.date()]),
    periodEnd: z.union([z.string(), z.date()]),
    notes: z.string().nullable().optional(),
    currentlyActive: z.boolean().optional(),
    createdAt: z.union([z.string(), z.date()]).optional(),
    updatedAt: z.union([z.string(), z.date()]).optional(),
    advance: EnterpriseAdvanceSchema,
  })
  .passthrough();

export const EnterpriseSeatSchema = z
  .object({
    userId: z.string(),
    email: z.string().optional(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    role: z.string().optional(),
    status: z.string().optional(),
    assignedAt: z.union([z.string(), z.date()]).optional(),
  })
  .passthrough();

export const EnterpriseStatusSchema = z
  .object({
    success: z.boolean().optional(),
    enabled: z.boolean(),
    plan: EnterprisePlanSchema.nullable(),
    seatsUsed: z.number(),
    seatsRemaining: z.number(),
    minutesUsed: z.number(),
    minutesRemaining: z.number().nullable(),
    seats: z.array(EnterpriseSeatSchema).optional(),
    advanceQuote: EnterpriseAdvanceQuoteSchema,
    advance: EnterpriseAdvanceQuoteSchema,
  })
  .passthrough();

export type EnterpriseStatus = z.infer<typeof EnterpriseStatusSchema>;

export const UpsertEnterpriseInputSchema = z.object({
  status: z.enum(["active", "suspended", "ended"]).default("active"),
  seatLimit: z.coerce.number().int().min(0),
  includedMinutes: z.coerce.number().int().min(0),
  periodStart: z.string().min(1),
  periodEnd: z.string().min(1),
  notes: z.string().optional().nullable(),
  /** Offline PO already collected — skip Stripe advance. */
  skipAdvanceCharge: z.boolean().optional(),
});

export type UpsertEnterpriseInput = z.infer<typeof UpsertEnterpriseInputSchema>;
